import dayjs from 'dayjs';

/**
 * Неразрывный пробел внутри терма: «1 ч 40 мин» в узкой плитке рвался посередине
 * («1 ч 40 / мин»); терм даты строки `compact` («с 01.01.2027 до 02.02.2027») — так
 * же. Терм — единое значение, переносится только по разделителю мета-строки (19342)
 */
export const NBSP = '\u00a0';

/**
 * Разделитель мета-строки проекта: визуально ` · ` контракта 19342, но пробел ПЕРЕД
 * точкой неразрывный — строка рвётся только после точки, и точка остаётся с
 * предыдущим термом («… 2 ч 24 мин ·» / «18+»), а не открывает следующую строку
 * (макет v2, §4)
 */
export const META_SEPARATOR = `${NBSP}· `;

/**
 * Формат дат проекта на вебе — полный год (решение 2026-10-15, макет v2): списки,
 * строки, плитки, страница проекта и блок проекта на кастинге печатают даты через
 * эту константу (`decisions/project-period-numeric-format.md`)
 */
const PROJECT_DATE_FORMAT = 'DD.MM.YYYY';

/**
 * Форматирует длительность фильма («1 ч 49 мин», «45 мин», «2 ч»), исходный
 * `movie_length` — целые минуты. Переехал из админки (19342). Все пробелы внутри
 * терма неразрывные (`NBSP`) — терм не рвётся при переносе мета-строки
 * @param value - сырое значение `movie_length`
 */
export const formatMovieLength = (value: unknown) => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) return '';

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const parts: string[] = [];
  if (hours) parts.push(`${hours}${NBSP}ч`);
  if (rest || !hours) parts.push(`${rest}${NBSP}мин`);

  return parts.join(NBSP);
};

/**
 * Форматирует возрастной рейтинг («6+», «0+»), исходный `age_rating` — число.
 * Переехал из админки (19342)
 * @param value - сырое значение `age_rating`
 */
export const formatAgeRating = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '';

  const rating = Number(value);
  return Number.isFinite(rating) ? `${rating}+` : '';
};

/**
 * Рейтинг Кинопоиска как в источнике: до двух знаков после точки, хвостовые нули
 * отброшены («5.37», «6.7», «8»). Округление до одного знака (было до волны 2
 * 19342) расходилось с цифрой на Кинопоиске. Бэк отдаёт число (`to_f`), строку не
 * разбираем; 0 и меньше, `NaN`/бесконечность — нет рейтинга
 * @param value - `rating_kp`
 */
export const formatRatingKp = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? String(Number(value.toFixed(2)))
    : '';

/**
 * Дата проекта в формате DD.MM.YYYY; пустая и нечитаемая — пустая строка
 * @param value - ISO-дата
 */
export const formatProjectDate = (value?: string | null) => {
  if (!value) return '';

  const date = dayjs(value);
  return date.isValid() ? date.format(PROJECT_DATE_FORMAT) : '';
};

/**
 * Период производства «с … до …». Только обе границы: одна дата периодом не
 * считается (как и до 19342)
 * @param start - начало периода
 * @param end - конец периода
 */
export const formatProjectPeriod = (
  start?: string | null,
  end?: string | null
): string | null => {
  const from = formatProjectDate(start);
  const to = formatProjectDate(end);

  return from && to ? `с ${from} до ${to}` : null;
};
